import { Injectable } from "@angular/core";

/** Data a Chromium `PasswordCredential` is constructed from. */
interface PasswordCredentialData {
  id: string;
  password: string;
}

/**
 * Chromium's `PasswordCredential` constructor. It is deliberately NOT part of
 * the standard TypeScript DOM lib because password credentials in the Credential
 * Management API are Chromium-only, so we type it ourselves and feature-detect
 * it at runtime.
 */
type PasswordCredentialConstructor = new (
  data: PasswordCredentialData,
) => Credential;

interface CredentialCapableWindow {
  PasswordCredential?: PasswordCredentialConstructor;
}

/**
 * Hands a freshly-authenticated credential to the browser's password manager so
 * it can offer to save it.
 *
 * Why this exists: login authenticates over an XHR (not a native form POST) and
 * then immediately fires a full-document, cross-origin redirect. That teardown
 * happens before the browser's automatic "save password?" heuristic can run, so
 * the prompt never appears. The Credential Management API lets us hand the
 * credential straight to the browser process, which takes ownership of it
 * independently of the renderer that is about to be destroyed — so the save
 * survives the redirect.
 *
 * This is Chromium-only (Chrome, Edge, and other Chromium browsers). On
 * Firefox/Safari `PasswordCredential` is undefined and this becomes a no-op;
 * those browsers rely instead on the form heuristic + autocomplete markup.
 */
@Injectable({ providedIn: "root" })
export class CredentialStorageService {
  /**
   * Stores the given credential in the browser's password manager (Chromium).
   *
   * Best-effort by design: every failure (password manager disabled by policy, a
   * credential operation already in flight, unsupported browser) is swallowed so
   * this can never block or break login. Resolves once the browser has taken
   * ownership of the credential, or immediately when unsupported.
   */
  public async store(userId: string, password: string): Promise<void> {
    const passwordCredentialCtor = (
      window as unknown as CredentialCapableWindow
    ).PasswordCredential;

    if (!passwordCredentialCtor || !navigator.credentials) {
      return;
    }

    try {
      await navigator.credentials.store(
        new passwordCredentialCtor({ id: userId, password }),
      );
    } catch {
      /* Password manager disabled by policy, or a credential operation is
         already in flight. Saving is a convenience, never a hard requirement,
         so we intentionally ignore the failure. */
    }
  }
}
